"use client"

import { type ReactNode } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Scissors, Droplets, DollarSign, Maximize2, Ruler, Sprout, FlaskConical } from "lucide-react"
import { type LawnSizeResult } from "@/lib/lawn-size-calculator"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import {
  CollapsibleSection,
} from "@/components/tools/shared"

interface LawnSizeResultsProps {
  result: LawnSizeResult
  /** Optional slot rendered at the peak — directly under the sqft hero, above
   *  the affiliate products — so the email ask sits at the highest-intent pixel. */
  emailCapture?: ReactNode
}

export function LawnSizeResults({ result, emailCapture }: LawnSizeResultsProps) {
  // Planning quantities with visible bag-size and rate assumptions. These are
  // intentionally not framed as exact because grass species and product labels
  // can require different application rates.
  // Measurements (acres, edging) live in the sub-line under the number;
  // equipment lives in the demoted row — so nothing is shown twice.
  const statChips = [
    {
      label: "Grass seed estimate",
      value: `${result.productQuantities.grassSeed.lbs} lbs`,
      sub: `about ${result.productQuantities.grassSeed.bags} × ${result.productQuantities.grassSeed.bagSizeLbs} lb ${result.productQuantities.grassSeed.bags === 1 ? "bag" : "bags"}`,
      icon: Sprout,
    },
    {
      label: "Fertilizer estimate",
      value: `${result.productQuantities.fertilizer.lbs} lbs`,
      sub: `about ${result.productQuantities.fertilizer.bags} × ${result.productQuantities.fertilizer.bagSizeLbs} lb ${result.productQuantities.fertilizer.bags === 1 ? "bag" : "bags"}`,
      icon: FlaskConical,
    },
  ]

  // Equipment recommendations — now a demoted secondary row under the numbers.
  const equipment = [
    { label: "Mower", value: result.mowerDeckSize.recommended, icon: Scissors },
    { label: "Spreader", value: result.spreaderCapacity.recommended, icon: Droplets },
    { label: "Annual budget", value: result.budgetEstimate.annual, icon: DollarSign },
  ]

  const recommendations = [
    {
      title: "Equipment Recommendations",
      icon: Scissors,
      items: result.mowerDeckSize.options.map(option =>
        `${option.type} (${option.size}): ${option.avgMowingTime} mowing time - ${option.suitability} for your lawn`
      )
    },
    {
      title: "Size-Specific Tips",
      icon: Ruler,
      items: result.sizeTips
    }
  ]


  return (
    <>
    <div className="space-y-8">
      {/* Hero Results Card — leads with the measured number and the actionable
          quantities (seed / fertilizer / edging / refills) as a dense stat grid;
          equipment recommendations are demoted to a secondary row below. */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-6">
          {/* Prominent area number */}
          <div className="mb-5">
            <p className="text-sm font-medium text-gray-500">Your lawn measures</p>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
                {result.totalAreaSqFt.toLocaleString()}
              </span>
              <span className="text-lg font-semibold text-gray-500">sq ft</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {result.totalAreaAcres.toLocaleString()} acres
              {result.totalPerimeterFt > 0 && <> · {result.totalPerimeterFt.toLocaleString()} ft around the edge</>}
            </p>
          </div>

          {/* The two consumables the user came for */}
          <div className="grid grid-cols-2 gap-3 min-w-0">
            {statChips.map((chip, i) => (
              <div key={i} className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-3">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <chip.icon className="w-4 h-4 text-green-600 flex-shrink-0" aria-hidden="true" />
                  <span className="text-xs font-medium text-gray-500 truncate">{chip.label}</span>
                </div>
                <div className="text-lg sm:text-xl font-bold text-gray-900 leading-tight break-words">{chip.value}</div>
                <p className="text-xs text-gray-500 mt-0.5 break-words">{chip.sub}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Peak-end email ask — sits directly under the sqft hero, above the
          affiliate products, so the highest-intent moment hosts the capture
          (growth-design: linkedin-notifications anticipation ask). Optional
          slot; nothing renders when the page doesn't pass it. */}
      {emailCapture}

      {/* Product Recommendations — surfaced directly under the hero so the bag
          counts below have an immediate shop-able path. Mower and smart-tech
          categories were the wrong draw: a reader who just measured a lawn is
          buying bags sized to that number, not a new mower (affiliate refresh
          2026-09-13, 2,024 pv/14d for 2 clicks). */}
      <ProductRecommendations
        categories={["Fertilizers & Soil Health", "Grass Seed & Lawn Repair"]}
        title="Products sized to your lawn"
        description={`Fertilizer and seed matched to a ${result.totalAreaSqFt.toLocaleString()} sq ft lawn`}
        trackingContext="lawn-size-calculator-hero"
        icon={<Maximize2 className="w-5 h-5 text-green-600" />}
        keywords={[
          'fertilizer',
          'grass seed',
          'starter',
          result.spreaderCapacity.recommended,
        ].filter(Boolean)}
        limit={3}
        compact
      />


      {/* Things to Know — collapsed accordion */}
      <CollapsibleSection
        title="Things to Know"
        icon={Ruler}
        defaultExpanded={false}
        storageKey="lawn-size-things-to-know"
      >
        <div className="space-y-5 pt-3">
          {/* Planning basis + equipment, relocated out of the hero card so the
              first result frame stays compact enough for the save ask to ride
              above the fold on phones. */}
          <div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {equipment.map((item, i) => (
                <div key={i} className="flex items-center gap-2 min-w-0">
                  <item.icon className="w-4 h-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
                  <span className="text-xs text-gray-500">{item.label}:</span>
                  <span className="text-sm font-semibold text-gray-700 truncate">{item.value}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Budget is a DIY materials estimate. For a region- and grass-aware figure, run the{" "}
              <a href="/tools/lawn-cost-estimator" className="text-green-700 underline hover:text-green-800">
                Lawn Cost Estimator
              </a>
              .
            </p>
            <p className="mt-2 text-xs leading-relaxed text-gray-500">
              Planning basis: seed at {result.productQuantities.grassSeed.rateLbsPer1000SqFt} lb per 1,000 sq ft;
              fertilizer product at {result.productQuantities.fertilizer.rateLbsPer1000SqFt} lb per 1,000 sq ft.
              Check your grass type and product label before buying or applying.
            </p>
          </div>

          {recommendations.map((rec, i) => (
            <div key={i}>
              <h4 className="flex items-center gap-2 font-semibold text-gray-800 text-sm mb-2">
                <rec.icon className="w-4 h-4 text-gray-600" />
                {rec.title}
              </h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-6">
                {rec.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="flex items-center gap-2 font-semibold text-gray-800 text-sm mb-2">
              <Maximize2 className="w-4 h-4 text-gray-600" />
              Measurement Details
            </h4>
            <div className="text-sm text-gray-600 ml-6 space-y-1">
              <p>Method: {result.measurementMethod === 'map_draw' ? 'Satellite Map Drawing' :
                       result.measurementMethod === 'manual_rectangle' ? 'Manual Rectangle' :
                       'Manual Irregular Shape'}</p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

    </div>
    </>
  )
}
